/**
 *  @swagger
 *  definitions:
 *    RegattaResultOverallEntryParams:
 *      type: object
 *      properties:
 *        RegattaResultOverallId:
 *          description: ID of the associated RegattaResultOverall
 *          type: string
 *        RegattaResultTableEntryId:
 *          description: ID of the associated RegattaResultTableEntry
 *          type: string
 *        Discarded:
 *          description: Discarded of the RegattaResultOverallEntry
 *          type: boolean
 *        Races:
 *          description: Races of the RegattaResultOverallEntry
 *          type: integer
 *        Place:
 *          description: Place of the RegattaResultOverallEntry
 *          type: string
 *        Points:
 *          description: Points of the RegattaResultOverallEntry
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverallEntryCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultOverallEntryParams'
 *        - type: object
 *          required:
 *            - RegattaResultOverallId
 *            - RegattaResultTableEntryId
 *            - Discarded
 *            - Races
 *            - Points
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverallEntryQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaResultOverallEntry
 *              type: string
 *        - $ref: '#/definitions/RegattaResultOverallEntryParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverallEntryUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultOverallEntryQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverallEntry:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultOverallEntryCreateParams'
 *        - $ref: '#/definitions/RegattaResultOverallEntryUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaResultOverallEntry:
 *        allOf:
 *          - $ref: '#/definitions/RegattaResultOverallEntry'
 *          - type: object
 *            properties:
 *              RegattaResultOverall:
 *                description: RegattaResultOverall of the RegattaResultOverallEntry
 *                $ref: '#/components/schemas/RegattaResultOverall'
 *              RegattaResultTableEntry:
 *                description: RegattaResultTableEntry of the RegattaResultOverallEntry
 *                $ref: '#/components/schemas/RegattaResultTableEntry'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaResultOverallId: string;
  RegattaResultTableEntryId: string;
};

export type $Params = {
  Discarded: boolean;
  Place: null | string;
  Points: number;
  Races: number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaResultOverall?: $Model<'RegattaResultOverall'>;
  RegattaResultTableEntry?: $Model<'RegattaResultTableEntry'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Discarded: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Place: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Points: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Races: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaResultOverallEntry'> => {
  const RegattaResultOverallEntry = stack.define(
    'RegattaResultOverallEntry',
    fields(Sequelize),
    {
      tableName: 'RegattaResultOverallEntries',
    },
  );

  RegattaResultOverallEntry.associate = (models: $ModelClasses) => {
    models.RegattaResultOverallEntry.belongsTo(
      models.RegattaResultOverall,
      {
        as: 'RegattaResultOverall',
        foreignKey: 'RegattaResultOverallId',
      },
    );
    models.RegattaResultOverallEntry.belongsTo(
      models.RegattaResultTableEntry,
      {
        as: 'RegattaResultTableEntry',
        foreignKey: 'RegattaResultTableEntryId',
      },
    );
  };

  RegattaResultOverallEntry.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaResultOverallEntry;
});
